import React from "react";
import { styled, useTheme } from "@material-ui/styles";
import { Grid, Chip, Box, Theme } from "@material-ui/core";
import Typography from "../typography";
import { IStyledArguments } from "../../types/styled-arguments";
import { IPlayer } from "../../types";
import Breadcrumb from "./Breadcrumb";
import EventIcon from "@material-ui/icons/Event";

interface IHeaderMatchV3 {
  match: any;
  matchStatus: string;
  score: any;
  scorerList?: { [index: string]: number };
}

const HeaderContainer = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  borderRadius: "16px",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  color: "white",
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.1)",
  "@media (min-width: 768px)": {
    padding: theme.spacing(3),
  },
}));

const MatchDate = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "rgba(255, 255, 255, 0.9)",
  marginBottom: "12px",
  fontSize: "0.8rem",
  "@media (min-width: 768px)": {
    fontSize: "0.95rem",
    marginBottom: "16px",
  },
});

const TeamsContainer = styled(Grid)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-around",
  marginTop: "16px",
  marginBottom: "16px",
  "@media (min-width: 768px)": {
    marginTop: "24px",
    marginBottom: "24px",
  },
});

const TeamBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: 1,
  padding: "4px",
  minHeight: "120px",
  justifyContent: "flex-start",
  "@media (min-width: 768px)": {
    padding: "16px",
    minHeight: "160px",
  },
});

const TeamName = styled(Typography.H5)({
  color: "white",
  fontWeight: 700,
  textAlign: "center",
  marginTop: "8px",
  fontSize: "0.9rem",
  lineHeight: 1.2,
  "@media (min-width: 768px)": {
    fontSize: "1.3rem",
    marginTop: "12px",
  },
});

const TeamBadge = styled("div")(() => ({
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.2rem",
  fontWeight: 700,
  color: "white",
  border: "2px solid rgba(255, 255, 255, 0.3)",
  "@media (min-width: 768px)": {
    width: "80px",
    height: "80px",
    fontSize: "2rem",
    border: "3px solid rgba(255, 255, 255, 0.3)",
  },
}));

const ScoreSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 8px",
  "@media (min-width: 768px)": {
    padding: "0 24px",
  },
});

const ScoreBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "8px",
  "@media (min-width: 768px)": {
    gap: "24px",
    marginBottom: "12px",
  },
});

const ScoreValue = styled(Typography.H3)({
  color: "white",
  fontWeight: 700,
  fontSize: "2rem",
  lineHeight: 1,
  minWidth: "40px",
  textAlign: "center",
  "@media (min-width: 768px)": {
    fontSize: "3rem",
    minWidth: "60px",
  },
});

const VSText = styled(Typography.H6)({
  color: "rgba(255, 255, 255, 0.7)",
  fontWeight: 600,
  fontSize: "1rem",
  "@media (min-width: 768px)": {
    fontSize: "1.2rem",
  },
});

const StatusChip = styled(Chip)(() => ({
  background: "rgba(255, 255, 255, 0.2)",
  color: "white",
  fontWeight: 700,
  border: "1px solid rgba(255, 255, 255, 0.3)",
  fontSize: "0.85rem",
  padding: "4px 8px",
}));

// Scorers Components
const ScorersSection = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  marginTop: "16px",
  gap: "12px",
  "@media (min-width: 768px)": {
    marginTop: "24px",
    gap: "16px",
  },
});

const ScorersList = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  "@media (min-width: 768px)": {
    gap: "6px",
  },
});

const ScorerItem = styled(Box)({
  background: "rgba(255, 255, 255, 0.15)",
  padding: "6px 10px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  transition: "all 0.3s ease",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.25)",
    transform: "translateX(4px)",
  },
  "@media (min-width: 768px)": {
    padding: "8px 12px",
    borderRadius: "10px",
    gap: "8px",
  },
});

const ScorerAvatar = styled(Box)({
  width: "28px",
  height: "28px",
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "white",
  flexShrink: 0,
  "@media (min-width: 768px)": {
    width: "32px",
    height: "32px",
    fontSize: "0.8rem",
  },
});

const ScorerInfo = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
});

const ScorerName = styled(Typography.Body)({
  color: "white",
  fontWeight: 700,
  fontSize: "0.75rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  margin: 0,
  "@media (min-width: 768px)": {
    fontSize: "0.9rem",
  },
});

const ScorerGoals = styled(Box)({
  background: "rgba(255, 255, 255, 0.3)",
  borderRadius: "6px",
  padding: "3px 8px",
  color: "white",
  fontWeight: 700,
  fontSize: "0.75rem",
  display: "flex",
  alignItems: "center",
  gap: "3px",
  flexShrink: 0,
  "@media (min-width: 768px)": {
    borderRadius: "8px",
    padding: "4px 10px",
    fontSize: "0.85rem",
    gap: "4px",
  },
});

const getStatusLabel = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    PREPARING: "Preparing",
    READY: "Ready to Start",
    ONGOING: "In Progress",
    FINISHED: "Finished",
  };
  return statusMap[status] || status;
};

const getTeamInitials = (teamName: string): string => {
  const words = teamName.split(" ");
  if (words.length >= 2) {
    return words[0].charAt(0) + words[1].charAt(0);
  }
  return teamName.substring(0, 2).toUpperCase();
};

const getPlayerInitials = (player: IPlayer): string => {
  const firstInitial = player.name?.charAt(0)?.toUpperCase() || "";
  const lastInitial = player.surname?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
};

const HeaderMatchV3 = ({ match, matchStatus, score, scorerList = {} }: IHeaderMatchV3) => {
  const theme = useTheme<Theme>();
  const homeTeamName = match.isHomeTeamLocal
    ? match.homeTeam.name
    : match.awayTeam;
  const awayTeamName = match.isHomeTeamLocal
    ? match.awayTeam
    : match.homeTeam.name;

  const homeScore = match.isHomeTeamLocal ? score.home : score.away;
  const awayScore = match.isHomeTeamLocal ? score.away : score.home;

  const showScore = matchStatus === "ONGOING" || matchStatus === "FINISHED";

  // Process scorers
  const scorers = match.playersConfirmed?.filter(
    (player: IPlayer) => scorerList[player.id] > 0
  ) || [];
  
  const sortedScorers = scorers.sort(
    (a: IPlayer, b: IPlayer) => scorerList[b.id] - scorerList[a.id]
  );

  const hasScorers = sortedScorers.length > 0 && (matchStatus === "ONGOING" || matchStatus === "FINISHED");

  return (
    <HeaderContainer>
      <Breadcrumb match={match} />
      
      <MatchDate>
        <EventIcon fontSize="small" />
        {match.date}
      </MatchDate>

      <TeamsContainer container>
        {/* Home Team */}
        <TeamBox>
          <TeamBadge>{getTeamInitials(homeTeamName)}</TeamBadge>
          <TeamName>{homeTeamName}</TeamName>
          {match.isHomeTeamLocal ? (
            <Chip
              size="small"
              label="Local"
              style={{
                marginTop: "8px",
                background: "rgba(76, 175, 80, 0.3)",
                color: "white",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          ) : (
            <Box style={{ height: "32px", marginTop: "8px" }} />
          )}
        </TeamBox>

        {/* Score or VS */}
        <ScoreSection>
          {showScore ? (
            <>
              <ScoreBox>
                <ScoreValue>{homeScore}</ScoreValue>
                <VSText>-</VSText>
                <ScoreValue>{awayScore}</ScoreValue>
              </ScoreBox>
              {matchStatus === "FINISHED" && (
                <img 
                  src="/images/footik-mark.svg"
                  alt="Footik" 
                  style={{ 
                    height: "40px", 
                    width: "auto",
                  }} 
                  className="footik-logo"
                />
              )}
            </>
          ) : (
            <VSText style={{ fontSize: "1.5rem", color: "white" }}>
              VS
            </VSText>
          )}
        </ScoreSection>

        <style>
          {`
            @media (min-width: 768px) {
              .footik-logo {
                height: 64px !important;
              }
            }
          `}
        </style>

        {/* Away Team */}
        <TeamBox>
          <TeamBadge>{getTeamInitials(awayTeamName)}</TeamBadge>
          <TeamName>{awayTeamName}</TeamName>
          {!match.isHomeTeamLocal ? (
            <Chip
              size="small"
              label="Local"
              style={{
                marginTop: "8px",
                background: "rgba(76, 175, 80, 0.3)",
                color: "white",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          ) : (
            <Box style={{ height: "32px", marginTop: "8px" }} />
          )}
        </TeamBox>
      </TeamsContainer>

      {/* Scorers Section */}
      {hasScorers && (
        <ScorersSection>
          <ScorersList>
            {sortedScorers.map((player: IPlayer) => (
              <ScorerItem key={player.id}>
                <ScorerAvatar>{getPlayerInitials(player)}</ScorerAvatar>
                <ScorerInfo>
                  <ScorerName>
                    {player.name} {player.surname}
                  </ScorerName>
                </ScorerInfo>
                <ScorerGoals>
                  <span>{scorerList[player.id]}</span>
                  <span>⚽</span>
                </ScorerGoals>
              </ScorerItem>
            ))}
          </ScorersList>
        </ScorersSection>
      )}

      <Box display="flex" justifyContent="center" mt={2}>
        <StatusChip
          label={getStatusLabel(matchStatus)}
          icon={matchStatus === "ONGOING" ? <Box
            component="span"
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: theme.palette.extra.success,
              animation: "pulse 2s infinite",
              display: "inline-block",
            }}
          /> : undefined}
        />
      </Box>

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              transform: scale(1.2);
            }
          }
        `}
      </style>
    </HeaderContainer>
  );
};

export default HeaderMatchV3;
